<script setup lang="ts">
import { REGULAR_EXPEDITIONS } from '~/shared/expedition'
import { WEB_APP_URL } from '~/constants/website'
import type { PricingResponseT } from '~/types/cekaja'

const props = defineProps<{
  item: PricingResponseT
  priority?: boolean
}>()

const isFlat = computed(() => props.item.service_type.startsWith('FL'))
const isDiscounted = computed(() => props.item.discount_campaign.discount > 0)
const discounted = computed(() => {
  if (isFlat.value) {
    return 0
  } else if (isDiscounted.value) {
    return (
      Number(props.item.cost) - Number(props.item.discount_campaign.discount)
    )
  }
  return 0
})

const courierImage = computed(() => {
  const findCourier = REGULAR_EXPEDITIONS.find(
    (expedition) => expedition.code === props.item.service
  )
  return {
    icon: findCourier?.icon,
    icon_class: findCourier?.class
  }
})

const buildUrlParams = (newParams: Record<string, string>) => {
  const urlParams = new URLSearchParams(window.location.search)
  Object.entries(newParams).forEach(([key, value]) => {
    urlParams.set(key, value)
  })
  return urlParams.toString()
}
</script>

<template>
  <div
    :class="
      cn(
        'border grid gap-2 w-full relative items-start rounded-xl transition-all bg-surface-container border-outline-variant p-3',
        {
          'ring-1 ring-primary bg-secondary-container border-primary':
            props.priority
        }
      )
    "
  >
    <div
      v-if="isFlat || isDiscounted"
      class="absolute left-0 top-1 z-1 -translate-x-2 text-xs px-1.5 py-0.5 rounded-tl-2xl rounded-r-2xl font-medium flex items-center gap-0.5"
      :class="{
        'bg-success text-on-success': isFlat,
        'bg-error text-on-error': !isFlat
      }"
    >
      <span
        class="w-2 h-1.5 rounded-bl-full absolute bottom-0 left-0 translate-y-1.5"
        :class="{
          'bg-success': isFlat,
          'bg-error': !isFlat
        }"
      ></span>
      <Icon
        :name="
          isFlat
            ? 'material-symbols:bolt-rounded'
            : 'material-symbols:percent-discount-outline'
        "
        size="16px"
      />
      {{ isFlat ? 'Flat Ongkir' : 'Lebih Hemat' }}
    </div>
    <div class="grid grid-cols-2 lg:flex relative gap-3 lg:items-center">
      <UiCourierService
        v-if="courierImage.icon"
        class="w-20"
        :icon="courierImage.icon"
        :icon-class="courierImage.icon_class"
      />
      <NuxtImg
        v-else
        class="w-20 aspect-video shrink-0 border bg-white border-outline-variant rounded-md object-fit p-2"
        :src="`https://dev-home.bakso.my.id/assets/home-v4/${props.item.service}.svg`"
      />
      <div class="grid gap-3 col-span-2 grid-cols-2 lg:grid-cols-5 grow">
        <div class="flex-1">
          <div class="text-on-surface-variant text-sm">Layanan</div>
          <div class="font-semibold text-sm">
            {{ props.item.service_name }}
          </div>
        </div>
        <div class="flex-1">
          <div class="text-on-surface-variant text-sm">Estimasi</div>
          <div class="font-semibold text-sm">
            {{ props?.item?.etd ? props?.item?.etd + ' Hari' : '-' }}
          </div>
        </div>
        <div class="flex-1">
          <div class="text-on-surface-variant text-sm">COD</div>
          <div class="font-semibold text-sm">
            {{ props.item.cod ? 'Tersedia' : 'Tidak Tersedia' }}
          </div>
        </div>
        <div class="flex-1">
          <div class="text-on-surface-variant text-sm">Opsi Penjemputan</div>
          <div class="font-semibold text-sm flex gap-1">
            <span>Pick Up</span>
            <span v-if="props.item.drop"> & Drop Off</span>
          </div>
        </div>
        <div class="flex-1">
          <div class="text-on-surface-variant text-sm">Tarif</div>
          <div class="font-semibold text-sm flex gap-1">
            <span
              :class="{
                'text-success': isFlat || isDiscounted
              }"
            >
              {{
                isDiscounted
                  ? discounted > 0
                    ? formatNumber(discounted, true)
                    : 'Gratis Ongkir'
                  : formatNumber(Number(props.item.cost), true)
              }}
            </span>
            <del v-if="isDiscounted" class="text-error">
              {{ formatNumber(Number(props.item.cost), true) }}
            </del>
          </div>
        </div>
      </div>

      <UiButton
        class="shrink-0 col-span-2 lg:ml-10"
        icon="material-symbols:arrow-outward"
        :variant="priority ? 'primary' : 'primaryOutline'"
        label="Kirim Paket"
        :to="
          $utm.utmUrl(
            WEB_APP_URL +
              '/register?' +
              buildUrlParams({
                courier: props.item.service + '~' + props.item.service_type,
                redirectTo: '/packages/create/regular'
              })
          )
        "
      />
    </div>
  </div>
</template>
